import { faEye, faEyeSlash, faLock, faSpinner } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { zodResolver } from "@hookform/resolvers/zod/dist/zod"
import { AbsolutCentered } from "AbsolutCentered/AbsolutCentered"
import { useClient } from "Client/ClientAndUserProvider"
import { onWheelRef } from "CustomerPortal/CustomerPortalCustomerDetails/CustomerPortalCustomerDetails"
import { exhaustive } from "exhaustive"
import { PenIcon } from "Icons/Icon"
import { isEmpty } from "lodash"
import { DateTime } from "luxon"
import { AccentButton, FinalizeButton } from "Orders/Components/Form/Buttons/Buttons"
import { ModulePopup } from "Orders/Components/ModulePopup/ModulePopup"
import { SbtH4 } from "Orders/Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "Orders/Components/Text/SbtInvalid/SbtRHFError"
import { PermissionAreaLocation, usePermissions } from "PermissionContext"
import { FC, useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { z } from "zod"
import { useAppIsFocused } from "../../../../AppIsFocusedContext"
import { CoredinationMode } from "../../../../Client/GetClientResponse"
import { Loader } from "../../../../Loader/Loader"
import { API, ServerError } from "../../../../network/API"
import orderConfirmStyle from "../../../../Orders/Components/OrderConfirmCustomerInformation/OrderConfirmCustomerInformation.module.css"
import consumerInfoStyle from "../../../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation.module.css"
import myAccountStyle from "../../../CustomerPortalMyAccount/CustomerPortalMyAccount.module.css"
import { ExternalIntegrationEvents } from "../../ExternalIntegrationEvents/ExternalIntegrationEvents"
import style from "./CoredinationSettings.module.css"
import {
	GetCoredinationSettings,
	GetCoredinationSettingsResponse,
	SetCoredinationSettingsRequest,
	SetCoredinationSettingsResponse,
	TestCoredinationSettingsRequest,
} from "./CoredinationSettingsModel"

type EditCoredinationSettingsProps = {
	settings: GetCoredinationSettings | null
	onClose: () => void
	onDone: (settings: GetCoredinationSettings | null) => void
}

const EditCoredinationSettingsFormSchema = z.object({
	apiKey: z.string().nullable(),
	newSecret: z.string().nullable(),
	editSecret: z.string().nullable(),
})

type EditCoredinationSettingsFormSchemaType = z.input<typeof EditCoredinationSettingsFormSchema>

const EditCoredinationSettings: FC<EditCoredinationSettingsProps> = ({ settings, onClose, onDone }) => {
	const client = useClient()
	const permissions = usePermissions()

	const [showSecret, setShowSecret] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [testedSettingsValid, setTestedSettingsValid] = useState<boolean | null>(null)
	const [submitError, setSubmitError] = useState<"generic" | "invalid_credentials" | null>(null)

	const isNew = settings === null

	const {
		register,
		handleSubmit,
		getValues,
		watch,
		formState: { errors, isDirty, isValid },
	} = useForm<EditCoredinationSettingsFormSchemaType>({
		resolver: async (data, context, options) => {
			return zodResolver(EditCoredinationSettingsFormSchema)(data, context, options)
		},
		defaultValues: {
			apiKey: settings?.apiKey,
			newSecret: isNew ? "" : null,
			editSecret: isNew ? null : "",
		},
	})

	async function onSubmit(data: EditCoredinationSettingsFormSchemaType) {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update)) {
			return
		}

		setSubmitting(true)
		setSubmitError(null)

		let req: SetCoredinationSettingsRequest

		if (!data.apiKey) {
			req = {
				coredinationSettings: null,
			}
		} else {
			let secret = data.newSecret
			if (secret === null) {
				if (isEmpty(data.editSecret)) {
					secret = null
				} else {
					secret = data.editSecret
				}
			}
			req = {
				coredinationSettings: {
					apiKey: data.apiKey,
					secret: secret,
				},
			}
		}

		API.postWithRetries<SetCoredinationSettingsResponse, SetCoredinationSettingsRequest>(
			`/customer-portal/coredination-settings-v1/${client.identifier}`,
			req,
			undefined,
			10,
		)
			.then((res) => {
				onDone(res.coredinationSettings || null)
			})
			.catch((error: ServerError<any>) => {
				setSubmitting(false)
				if (error?.data?.message && error.data.message === "invalid_credentials") {
					setSubmitError("invalid_credentials")
				} else {
					setSubmitError("generic")
				}
			})
	}

	async function testSettings() {
		if (
			!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update) ||
			!testSettingsButtonEnabled()
		) {
			return
		}

		setSubmitting(true)
		setTestedSettingsValid(null)
		setSubmitError(null)

		const apiKey = getValues("apiKey")
		const secret = isNew ? getValues("newSecret") : getValues("editSecret")

		if (!apiKey || !secret) {
			setSubmitting(false)
			return
		}

		let req: TestCoredinationSettingsRequest = {
			apiKey: apiKey,
			secret: secret,
		}

		API.postWithRetries<void, TestCoredinationSettingsRequest>(
			`/customer-portal/coredination-settings-v1/${client.identifier}/test`,
			req,
			undefined,
			10,
		)
			.then((res) => {
				setSubmitting(false)
				setTestedSettingsValid(true)
			})
			.catch(() => {
				setSubmitting(false)
				setTestedSettingsValid(false)
			})
	}

	function submitErrorToHumanText(submitError: "generic" | "invalid_credentials") {
		return exhaustive(submitError, {
			invalid_credentials: () => "Felaktiga uppgifter, vänlien försök igen",
			generic: () => "Något gick fel, vänligen försök igen",
		})
	}

	function testSettingsButtonEnabled() {
		if (submitting) {
			return false
		}

		if (isNew) {
			return !!watch("apiKey") && !!watch("newSecret")
		} else {
			return !!watch("apiKey") && !!watch("editSecret")
		}
	}

	if (!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update)) {
		return null
	}

	return (
		<ModulePopup className={style.modal} onClose={onClose} disableCloseWithEsc={submitting}>
			<div className={style.wrapper}>
				<div
					className={consumerInfoStyle.headerText}
					style={{ marginTop: "-15px", marginBottom: "35px", marginRight: "30px" }}>
					Redigera coredinationinställningar
				</div>
				<form className={style.form} onSubmit={handleSubmit((data) => onSubmit(data))} autoComplete="off">
					<div className={orderConfirmStyle.fields}>
						<label>
							<SbtH4>API-nyckel</SbtH4>
							<input
								className={orderConfirmStyle.input}
								disabled={submitting}
								{...register("apiKey")}
								placeholder="3b7c88f7-16d0-4620-8927-9c286ef85c18"
							/>
							<SbtRHFError error={errors.apiKey} />
						</label>
						<label>
							<SbtH4>Secret{isNew ? "*" : null}</SbtH4>
							<div className={style.inputWrapper}>
								<div>
									<FontAwesomeIcon icon={faLock} />
								</div>
								<input
									className={orderConfirmStyle.input}
									disabled={submitting}
									type={showSecret ? "text" : "password"}
									{...register(isNew ? "newSecret" : "editSecret")}
									autoComplete="off"
									placeholder="API-secret"
								/>
								<FontAwesomeIcon
									style={{ marginLeft: "-33px", cursor: "pointer" }}
									icon={showSecret ? faEyeSlash : faEye}
									onClick={() => {
										setShowSecret(!showSecret)
									}}
								/>
							</div>
							<SbtRHFError error={isNew ? errors.newSecret : errors.editSecret} />
						</label>
					</div>
					<div className={style.finalizeWrapper}>
						<FinalizeButton
							className={style.finalize}
							disabled={submitting || !isDirty || !isValid}
							type="submit">
							Spara{" "}
							{submitting ? <FontAwesomeIcon style={{ color: "gray" }} spin={true} icon={faSpinner} /> : null}
						</FinalizeButton>
						<AccentButton
							className={style.finalize}
							disabled={!testSettingsButtonEnabled()}
							onClick={() => {
								testSettings()
							}}
							type="button">
							Testa{" "}
							{submitting ? <FontAwesomeIcon style={{ color: "gray" }} spin={true} icon={faSpinner} /> : null}
						</AccentButton>
					</div>

					{testedSettingsValid === true ? (
						<div style={{ color: "var(--valid-color)", margin: "15px 0 10px 0" }}>Korrekta uppgifter :)</div>
					) : null}

					{testedSettingsValid === false ? (
						<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
							Felaktiga uppgifter, vänligen försök igen
						</div>
					) : null}

					{submitError ? (
						<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
							{submitErrorToHumanText(submitError)}
						</div>
					) : null}
				</form>
			</div>
		</ModulePopup>
	)
}

type GetLastFullSyncEvent = {
	id?: string
	timestampMillisecond?: number
}

export const CoredinationSettings: FC = () => {
	const client = useClient()
	const permissions = usePermissions()
	const focused = useAppIsFocused()
	const isFocused = useRef(focused)
	isFocused.current = focused

	const [queryParams, setQueryParams] = useSearchParams()

	const [disableSubmit, setDisableSubmit] = useState(false)

	const [coredinationSettings, setCoredinationSettings] = useState<GetCoredinationSettings | "None" | null | "Loading">(
		"Loading",
	)
	const [edit, setEdit] = useState<boolean>(false)

	const [lastFullSyncEventTime, setLastFullSyncEventTime] = useState<number | null>(null)
	const [isFullSyncOngoing, setIsFullSyncOngoing] = useState<boolean | null>(true)

	const lastSyncTimer = useRef<NodeJS.Timeout | null>(null)
	const synOngoingTimer = useRef<NodeJS.Timeout | null>(null)

	const fetchLastSyncEvent = useCallback(() => {
		if (!isFocused.current) {
			lastSyncTimer.current = setTimeout(() => {
				fetchLastSyncEvent()
			}, 5000)

			return
		}

		API.getWithRetries<GetLastFullSyncEvent>(
			`/customer-portal/coredination-sync-v1/${client.identifier}/last-sync`,
			true,
		)
			.then((response) => {
				if (!response.timestampMillisecond) {
					lastSyncTimer.current = setTimeout(() => {
						fetchLastSyncEvent()
					}, 5000)
				} else {
					setLastFullSyncEventTime(response.timestampMillisecond)
				}
			})
			.catch(() => {})
	}, [client.identifier])

	const fetchIsFullSyncOngoing = useCallback(() => {
		if (!isFocused.current) {
			synOngoingTimer.current = setTimeout(() => {
				fetchIsFullSyncOngoing()
			}, 10000)

			return
		}

		API.getWithRetries<boolean>(
			`/customer-portal/coredination-sync-v1/${client.identifier}/is-full-sync-in-progress`,
			true,
		)
			.then((response) => {
				setIsFullSyncOngoing(response)
				synOngoingTimer.current = setTimeout(() => {
					fetchIsFullSyncOngoing()
				}, 10000)
			})
			.catch(() => {})
	}, [client.identifier])

	useEffect(() => {
		return () => {
			const timer1 = lastSyncTimer.current
			const timer2 = synOngoingTimer.current
			if (timer1 !== null) {
				clearTimeout(timer1)
			}

			if (timer2 !== null) {
				clearTimeout(timer2)
			}
		}
	}, [])

	useEffect(() => {
		if (
			client.externalIntegrationSettings.coredination.mode === CoredinationMode.Disabled ||
			!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)
		) {
			return
		}
		getSettings()
	}, [client.externalIntegrationSettings.coredination.mode, client.identifier, permissions])

	useEffect(() => {
		if (
			!client.hasFullCoredinationIntegrationEnabled() ||
			!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)
		) {
			return
		}

		fetchLastSyncEvent()
		fetchIsFullSyncOngoing()
	}, [client, permissions])

	function getSettings() {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)) {
			return
		}

		API.getWithRetries<GetCoredinationSettingsResponse>(
			`/customer-portal/coredination-settings-v1/${client.identifier}`,
			true,
		)
			.then((response) => {
				setCoredinationSettings(response?.coredinationSettings ?? "None")
			})
			.catch(() => {
				setCoredinationSettings(null)
			})
	}

	function startSync() {
		setDisableSubmit(true)
		API.post(`/customer-portal/coredination-sync-v1/${client.identifier}/start-sync`).then(
			() => {},
			() => {
				setDisableSubmit(false)
			},
		)
	}

	function syncSection() {
		if (
			!client.hasFullCoredinationIntegrationEnabled() ||
			!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read)
		) {
			return null
		}

		const coredinationSettingsSet =
			coredinationSettings !== null &&
			coredinationSettings !== "None" &&
			coredinationSettings !== "Loading" &&
			!!coredinationSettings.apiKey

		if (!coredinationSettingsSet) {
			return null
		}

		return (
			<div className={consumerInfoStyle.wrapper}>
				<div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
					<div className={consumerInfoStyle.headerText}>Synkronisering av data</div>
				</div>
				<div className={style.syncSection}>
					<FinalizeButton
						disabled={disableSubmit || isFullSyncOngoing === true}
						className={style.startSyncButton}
						onClick={() => {
							startSync()
						}}>
						Starta full synkronisering{" "}
						{disableSubmit || isFullSyncOngoing === true ? (
							<FontAwesomeIcon spin={true} icon={faSpinner} />
						) : null}
					</FinalizeButton>

					{isFullSyncOngoing === true ? (
						<div style={{ marginTop: "5px", fontWeight: 600 }}>Synkronisering pågår</div>
					) : null}
					<br />
					<div>
						<div>Senaste synkronisering</div>
						{lastFullSyncEventTime ? (
							<div>
								<div>
									Tid:{" "}
									{DateTime.fromMillis(lastFullSyncEventTime)
										.setLocale("sv-SE")
										.toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)}
								</div>
							</div>
						) : (
							<div>
								<div>Körd: Aldrig</div>
							</div>
						)}
					</div>

					<AccentButton
						className={style.viewEventsButton}
						onClick={() => {
							queryParams.set("showEvents", "")
							setQueryParams(queryParams)
						}}>
						Visa händelser
					</AccentButton>
				</div>
			</div>
		)
	}

	function content() {
		if (
			client.externalIntegrationSettings.coredination.mode === CoredinationMode.Disabled ||
			!permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Read) ||
			coredinationSettings === null
		) {
			return null
		}

		if (coredinationSettings === "Loading") {
			return (
				<div className={consumerInfoStyle.wrapper} style={{ minHeight: "250px" }}>
					<AbsolutCentered>
						<Loader />
					</AbsolutCentered>
				</div>
			)
		}

		if (queryParams.has("showEvents")) {
			return (
				<ExternalIntegrationEvents
					onCancel={() => {
						queryParams.delete("showEvents")
						queryParams.delete("page")
						queryParams.delete("pageSize")
						setQueryParams(queryParams)
					}}
				/>
			)
		}

		return (
			<>
				{permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update) && edit ? (
					<EditCoredinationSettings
						settings={coredinationSettings !== "None" ? coredinationSettings : null}
						onDone={() => {
							getSettings()
							setEdit(false)
						}}
						onClose={() => {
							setEdit(false)
						}}
					/>
				) : null}
				{syncSection()}
				<div className={consumerInfoStyle.wrapper}>
					<div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
						<div className={consumerInfoStyle.headerText}>Coredinationinställningar</div>
						{permissions.isAllowed(PermissionAreaLocation.Client_Third_Party_Integrations_Update) ? (
							<div>
								<PenIcon
									size={22}
									iconClassName={myAccountStyle.editIcon}
									onClick={() => {
										setEdit(true)
									}}
								/>
							</div>
						) : null}
					</div>
					<div style={{ marginTop: "35px" }}>
						<div className={consumerInfoStyle.textInputWithLabel}>
							<label>API-nyckel</label>
							<span
								ref={onWheelRef}
								className={consumerInfoStyle.textAsInput}
								title={coredinationSettings !== "None" ? coredinationSettings.apiKey : undefined}>
								{coredinationSettings !== "None" ? coredinationSettings.apiKey : null}
							</span>
						</div>
					</div>
				</div>
			</>
		)
	}

	return content()
}
